import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'
import FieldHelp from '../../components/FieldHelp'

interface Supplier {
  id: string
  name: string
  supplierCode?: string
  companyName?: string
  contactName?: string
  email?: string
  phone?: string
  address?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  taxId?: string
  paymentTerms?: string
  currency?: string
  isActive: boolean
  notes?: string
  createdAt: string
  creator: {
    firstName: string
    lastName: string
  }
  purchaseInvoices?: Array<{
    id: string
    invoiceNumber: string
    invoiceDate: string
    total: string
    status: string
  }>
}

const Suppliers: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    supplierCode: '',
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'GB',
    taxId: '',
    paymentTerms: '',
    currency: 'GBP',
    notes: '',
  })

  useEffect(() => {
    fetchSuppliers()
  }, [])

  const fetchSuppliers = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/suppliers', {
        params: { includeInactive: 'true' },
      })
      setSuppliers(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching suppliers:', error)
      toast.error('Failed to load suppliers')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required')
      return
    }

    try {
      await axios.post('/api/erp/suppliers', formData)
      toast.success('Supplier created successfully')
      setShowCreateModal(false)
      resetForm()
      fetchSuppliers()
    } catch (error: any) {
      console.error('Error creating supplier:', error)
      toast.error(error.response?.data?.error || 'Failed to create supplier')
    }
  }

  const handleView = async (id: string) => {
    try {
      const response = await axios.get(`/api/erp/suppliers/${id}`)
      setSelectedSupplier(response.data.data)
      setShowViewModal(true)
    } catch (error: any) {
      console.error('Error fetching supplier:', error)
      toast.error('Failed to load supplier')
    }
  }

  const handleEdit = (supplier: Supplier) => {
    setFormData({
      name: supplier.name,
      supplierCode: supplier.supplierCode || '',
      companyName: supplier.companyName || '',
      contactName: supplier.contactName || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      city: supplier.city || '',
      state: supplier.state || '',
      postalCode: supplier.postalCode || '',
      country: supplier.country || 'GB',
      taxId: supplier.taxId || '',
      paymentTerms: supplier.paymentTerms || '',
      currency: supplier.currency || 'GBP',
      notes: supplier.notes || '',
    })
    setSelectedSupplier(supplier)
    setShowCreateModal(true)
  }

  const handleUpdate = async () => {
    if (!selectedSupplier) return

    if (!formData.name.trim()) {
      toast.error('Name is required')
      return
    }

    try {
      await axios.put(`/api/erp/suppliers/${selectedSupplier.id}`, formData)
      toast.success('Supplier updated successfully')
      setShowCreateModal(false)
      setSelectedSupplier(null)
      resetForm()
      fetchSuppliers()
    } catch (error: any) {
      console.error('Error updating supplier:', error)
      toast.error(error.response?.data?.error || 'Failed to update supplier')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this supplier?')) {
      return
    }

    try {
      await axios.delete(`/api/erp/suppliers/${id}`)
      toast.success('Supplier deleted')
      fetchSuppliers()
    } catch (error: any) {
      console.error('Error deleting supplier:', error)
      toast.error(error.response?.data?.error || 'Failed to delete supplier')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      supplierCode: '',
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'GB',
      taxId: '',
      paymentTerms: '',
      currency: 'GBP',
      notes: '',
    })
    setSelectedSupplier(null)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading permissions...</div>
  }

  if (!hasPermission('erp_purchase_invoice', 'read')) {
    return <div className="p-6">You don't have permission to view suppliers.</div>
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-900">Suppliers</h1>
          <PageHelp pageIdentifier="suppliers" title="Suppliers" />
        </div>
        {hasPermission('erp_purchase_invoice', 'write') && (
          <button
            onClick={() => {
              resetForm()
              setShowCreateModal(true)
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Supplier
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading suppliers...</p>
        </div>
      ) : suppliers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-600">No suppliers found.</p>
          {hasPermission('erp_purchase_invoice', 'write') && (
            <button
              onClick={() => {
                resetForm()
                setShowCreateModal(true)
              }}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Your First Supplier
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {suppliers.map((supplier) => (
                <tr key={supplier.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{supplier.name}</div>
                    {supplier.companyName && (
                      <div className="text-sm text-gray-500">{supplier.companyName}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{supplier.supplierCode || '-'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{supplier.email || '-'}</div>
                    {supplier.phone && (
                      <div className="text-sm text-gray-500">{supplier.phone}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      supplier.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {supplier.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleView(supplier.id)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      View
                    </button>
                    {hasPermission('erp_purchase_invoice', 'write') && (
                      <>
                        <button
                          onClick={() => handleEdit(supplier)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        {hasPermission('erp_purchase_invoice', 'delete') && (
                          <button
                            onClick={() => handleDelete(supplier.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {selectedSupplier ? 'Edit Supplier' : 'Create Supplier'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  resetForm()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                    <FieldHelp content="The supplier's name or company name. This is the primary identifier." />
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Supplier name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier Code
                    <FieldHelp content="Optional unique code for the supplier (e.g., SUP-001). If left blank, a code will be auto-generated." />
                  </label>
                  <input
                    type="text"
                    value={formData.supplierCode}
                    onChange={(e) => setFormData({ ...formData, supplierCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="SUP-001"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Company name (if different from name)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Primary contact person"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="supplier@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="+44 123 456 7890"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Street address"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="City"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State/County
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="State or County"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Postcode"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="GB"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax ID / VAT Number
                    <FieldHelp content="The supplier's tax identification number (e.g., VAT number, GST number). Required for tax reporting." />
                  </label>
                  <input
                    type="text"
                    value={formData.taxId}
                    onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="GB123456789"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Terms
                    <FieldHelp content="Standard payment terms for this supplier (e.g., 'Net 30', 'Due on Receipt', '2/10 Net 30')." />
                  </label>
                  <input
                    type="text"
                    value={formData.paymentTerms}
                    onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Net 30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="CAD">CAD (C$)</option>
                    <option value="AUD">AUD (A$)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                  placeholder="Additional notes about this supplier"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  resetForm()
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={selectedSupplier ? handleUpdate : handleCreate}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {selectedSupplier ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedSupplier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{selectedSupplier.name}</h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Supplier Code</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedSupplier.supplierCode || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedSupplier.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedSupplier.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
                {selectedSupplier.companyName && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Company Name</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSupplier.companyName}</p>
                  </div>
                )}
                {selectedSupplier.contactName && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Contact Name</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSupplier.contactName}</p>
                  </div>
                )}
                {selectedSupplier.email && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSupplier.email}</p>
                  </div>
                )}
                {selectedSupplier.phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Phone</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSupplier.phone}</p>
                  </div>
                )}
                {selectedSupplier.taxId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Tax ID / VAT Number</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSupplier.taxId}</p>
                  </div>
                )}
                {selectedSupplier.paymentTerms && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Payment Terms</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedSupplier.paymentTerms}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-500">Currency</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedSupplier.currency}</p>
                </div>
              </div>

              {(selectedSupplier.address || selectedSupplier.city || selectedSupplier.postalCode) && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Address</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {[
                      selectedSupplier.address,
                      selectedSupplier.city,
                      selectedSupplier.state,
                      selectedSupplier.postalCode,
                      selectedSupplier.country
                    ].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}

              {selectedSupplier.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Notes</label>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedSupplier.notes}</p>
                </div>
              )}

              {selectedSupplier.purchaseInvoices && selectedSupplier.purchaseInvoices.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Recent Purchase Invoices</label>
                  <div className="space-y-2">
                    {selectedSupplier.purchaseInvoices.map((invoice) => (
                      <div key={invoice.id} className="p-3 bg-gray-50 rounded">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">{invoice.invoiceNumber}</span>
                          <span className="text-sm text-gray-500">{formatDate(invoice.invoiceDate)}</span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {parseFloat(invoice.total).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} - {invoice.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              {hasPermission('erp_purchase_invoice', 'write') && (
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    handleEdit(selectedSupplier)
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Edit
                </button>
              )}
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Suppliers
